import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import { InfoCircle, HamburgerMenu as MenuIcon, Home, Star } from "solar-icon-set";
import Container from "@mui/material/Container";
import { Button, MenuItem } from "@mui/material";
import { MessageRounded } from "@mui/icons-material";

interface NavbarProps {
  homeRef: React.RefObject<HTMLElement>;
  aboutRef: React.RefObject<HTMLElement>;
  expertiseRef: React.RefObject<HTMLElement>;
  contactMeRef: React.RefObject<HTMLElement>;
}

const Navbar: React.FC<NavbarProps> = ({
  homeRef,
  aboutRef,
  expertiseRef,
  contactMeRef,
}) => {
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
  const [activePath, setActivePath] = React.useState<string>("Home");

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const scrollToSection = (
    ref: React.RefObject<HTMLElement>,
    path: string
  ) => {
    if (ref.current) {
      const appBarHeight =
        document.querySelector(".MuiAppBar-root")?.clientHeight || 0;
      const top = ref.current.offsetTop - appBarHeight;
      window.scrollTo({ top, behavior: "smooth" });
      setActivePath(path);
      handleCloseNavMenu();
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: "#37393a",
        padding: "5px 10px",
        borderRadius: "10px",
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <IconButton edge="start" color="inherit" aria-label="logo">
            <img
              style={{ width: "50px", height: "50px" }}
              src="/images/logo.png"
              alt="logo"
              className="header-logo"
            />
          </IconButton>

          {/* Mobile Menu Icon */}
          <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-label="menu"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: "block", md: "none" } }}
            >
              <MenuItem onClick={() => scrollToSection(homeRef, "Home")}>
                <Typography textAlign="center">Home</Typography>
              </MenuItem>
              <MenuItem onClick={() => scrollToSection(aboutRef, "About")}>
                <Typography textAlign="center">About</Typography>
              </MenuItem>
              <MenuItem onClick={() => scrollToSection(expertiseRef, "Expertise")}>
                <Typography textAlign="center">Expertise</Typography>
              </MenuItem>
              <MenuItem onClick={() => scrollToSection(contactMeRef, "Contactme")}>
                <Typography textAlign="center">Contact Me</Typography>
              </MenuItem>
            </Menu>
          </Box>

          {/* Desktop Menu */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "none", md: "flex" },
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              variant="text"
              startIcon={<Home />}
              color="inherit"
              onClick={() => scrollToSection(homeRef, "Home")}
              sx={{
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: activePath === "Home" ? "darkgray" : "lightgray",
                },
                paddingLeft: "10px",
                paddingRight: "10px",
              }}
            >
              Home
            </Button>
            <Button
              variant="text"
              startIcon={<InfoCircle />}
              color="inherit"
              onClick={() => scrollToSection(aboutRef, "About")}
              sx={{
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: activePath === "About" ? "darkgray" : "lightgray",
                },
                paddingLeft: "10px",
                paddingRight: "10px",
              }}
            >
              About
            </Button>
            <Button
              variant="text"
              startIcon={<Star />}
              color="inherit"
              onClick={() => scrollToSection(expertiseRef, "Expertise")}
              sx={{
                marginRight: "20px",
                "&:hover": {
                  backgroundColor: activePath === "Expertise" ? "darkgray" : "lightgray",
                },
                paddingLeft: "10px",
                paddingRight: "10px",
              }}
            >
              Expertise
            </Button>
          </Box>

          {/* Contact Me Button */}
          <Box sx={{ flexGrow: 0 }}>
            <Button
              variant="contained"
              startIcon={<MessageRounded />}
              onClick={() => scrollToSection(contactMeRef, "Contactme")}
              sx={{
                paddingRight: "30px",
                paddingLeft: "30px",
                "&:hover": {
                  backgroundColor: activePath === "Contactme" ? "darkgray" : "lightgray",
                },
                color: "black",
                borderRadius: "50px",
                textTransform: "none",
              }}
            >
              Contact Me
            </Button>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
